/**
 * The long-press radial ring on open water. Holding a finger on clear water pops
 * a ring of location actions under the thumb, the things we do TO a spot rather
 * than to a charted object: make it the destination, drop a mark, make a note,
 * start a route from it, or read the wind there. Anchor watch lives in Safety &
 * Alarms and range rings under Layers & Chart, so neither clutters the ring.
 * Tapping a charted object still opens its detail sheet; the ring is the
 * open-water counterpart, so the two never overlap.
 *
 * This module is the pure half: the action list and the geometry that lays the
 * items evenly around the press point. The React ring renders from it and the
 * dispatch wires each action to the real stores.
 */
export type RingActionId =
  | 'destination'
  | 'mark'
  | 'makenote'
  | 'measure'
  | 'route'
  | 'wind'
  | 'tidehere'
  | 'whatshere'
  // Object-ring actions, offered when the press lands on a charted object.
  | 'details'
  | 'transit'
  | 'clearing';

export interface RingAction {
  id: RingActionId;
  label: string;
  /** Short glyph hint for the icon component. */
  glyph: string;
}

/** The open-water actions, in ring order (clockwise from the top). */
export const RING_ACTIONS: RingAction[] = [
  { id: 'destination', label: 'Set destination', glyph: 'flag' },
  { id: 'mark', label: 'Drop mark', glyph: 'pin' },
  { id: 'makenote', label: 'Make note', glyph: 'note' },
  { id: 'measure', label: 'Measure', glyph: 'measure' },
  { id: 'route', label: 'Route from here', glyph: 'route' },
  { id: 'wind', label: 'Wind here', glyph: 'wind' },
  // Straight from the chart to the curve for this water. The tide is the thing
  // we check most often and it was three taps away through the menu.
  { id: 'tidehere', label: 'Tide here', glyph: 'tide' },
  { id: 'whatshere', label: "What's here?", glyph: 'info' },
];

/**
 * The actions offered when the long-press lands ON a charted object (a buoy,
 * beacon, light, landmark and the like). Object-specific work comes first, the
 * things we do WITH the object: read its details, plot a transit through it,
 * hang a clearing bearing off it, or make it the destination. Then the
 * generally-useful spot actions (drop a mark, make a note, measure). Capped at
 * seven so the ring stays as tidy as the open-water one.
 */
export const OBJECT_RING_ACTIONS: RingAction[] = [
  { id: 'details', label: 'Details', glyph: 'info' },
  { id: 'transit', label: 'Plot transit', glyph: 'transit' },
  { id: 'clearing', label: 'Clearing bearing', glyph: 'clearing' },
  { id: 'destination', label: 'Set destination', glyph: 'flag' },
  { id: 'mark', label: 'Drop mark', glyph: 'pin' },
  { id: 'makenote', label: 'Make note', glyph: 'note' },
  { id: 'measure', label: 'Measure', glyph: 'measure' },
];

/**
 * The offset (px) of item `index` of `count`, laid evenly around a circle of
 * radius `radiusPx`, starting at the top (12 o'clock) and going clockwise. Screen
 * y grows downward, so the top item is at dy = −radius.
 */
export function ringItemOffset(
  index: number,
  count: number,
  radiusPx: number,
): { dx: number; dy: number } {
  const angle = -Math.PI / 2 + (index / count) * 2 * Math.PI;
  return {
    dx: Math.cos(angle) * radiusPx,
    dy: Math.sin(angle) * radiusPx,
  };
}

/** All item offsets for a ring of `count` items at `radiusPx`. */
export function ringLayout(count: number, radiusPx: number): { dx: number; dy: number }[] {
  return Array.from({ length: count }, (_, i) => ringItemOffset(i, count, radiusPx));
}
